import { useQuery } from "@tanstack/react-query";
import { getOverview } from "@/api/endpoints";
import { Card, PageTitle } from "@/components/ui/primitives";
import { DataState } from "@/components/data-state";
import { useAuth } from "@/hooks/use-auth";
import { useI18n } from "@/i18n";
import type { TranslationKey } from "@/i18n/translations";

export function OverviewPage() {
  const { t } = useI18n();
  const { activeOrgId } = useAuth();
  // The organisation belongs in the key: without it, switching organisations
  // shows the previous one's counts out of cache until something refetches.
  const query = useQuery({
    queryKey: ["overview", activeOrgId],
    queryFn: getOverview,
  });

  const tiles: { key: TranslationKey; value: number; alert?: boolean }[] = query.data
    ? [
        { key: "statCompanies", value: query.data.companies },
        { key: "statProducts", value: query.data.products },
        { key: "statSamples", value: query.data.samples },
        { key: "statOpenTasks", value: query.data.openTasks },
        { key: "statOverdueTasks", value: query.data.overdueTasks, alert: true },
        {
          key: "statExpiringCertificates",
          value: query.data.expiringCertificates,
          alert: true,
        },
      ]
    : [];

  return (
    <div className="space-y-4">
      <PageTitle>{t("navOverview")}</PageTitle>
      <DataState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={false}
        onRetry={() => void query.refetch()}
      >
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
          {tiles.map((tile) => (
            <Card key={tile.key} className="p-4">
              <p
                className={
                  tile.alert && tile.value > 0
                    ? "text-2xl font-bold text-destructive"
                    : "text-2xl font-bold"
                }
              >
                {tile.value}
              </p>
              <p className="text-sm text-muted-foreground">{t(tile.key)}</p>
            </Card>
          ))}
        </div>
      </DataState>
    </div>
  );
}
